'use client';

import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useGetDirectChatMessagesQuery,
  useGetGeneralChatMessagesQuery,
  useGetJobChatMessagesQuery,
  useGetMentionChatMessagesQuery,
  useOpenDirectMessageMutation,
  useGetStreamChatMessagesQuery,
  useGetUnreadChatMessagesQuery,
  useMarkChatConversationReadMutation,
} from '@/features/chat/api/chatApi';
import { useChatRealtimeSubscription } from '@/features/chat/realtime/ChatRealtimeProvider';
import { UserProfileDrawer } from '@/features/users/components';
import { useAppSelector } from '@/store/hooks';
import { Badge, Tooltip } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { ChatComposer } from './ChatComposer';
import { ChatMessageList } from './ChatMessageList';
import styles from './ChatPanel.module.scss';

import type {
  ActiveChat,
  ChatFilters,
} from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type {
  ChatConversation,
  ChatContextTagPayload,
  ChatMessage,
  CreateChatMessagePayload,
} from '@/features/chat/types';

type OptimisticMessage = ChatMessage & {
  isOptimistic?: boolean;
  chatKey: string;
};

type ReplyState = {
  chatKey: string;
  message: ChatMessage | null;
};

function getChatEyebrow(activeChat: ActiveChat) {
  switch (activeChat.type) {
    case 'general_channel':
      return 'General';
    case 'job_channel':
      return 'Job Channel';
    case 'direct_message':
      return 'Direct Message';
    case 'unread':
      return 'Unread';
    case 'mentions':
      return 'Mentions';
    default:
      return 'Chat';
  }
}

function isReadableConversation(activeChat: ActiveChat) {
  return ['general_channel', 'job_channel', 'direct_message'].includes(
    activeChat.type,
  );
}

function getConversationReadId(
  activeChat: ActiveChat,
  messages: ChatMessage[],
) {
  if (!isReadableConversation(activeChat)) {
    return null;
  }

  if (activeChat.type === 'general_channel' && activeChat.id === 'general') {
    return messages[0]?.source.id ?? null;
  }

  return activeChat.id;
}

function getTemporaryMessageId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `temp-${crypto.randomUUID()}`;
  }

  return `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getBodyText(body: string) {
  return body
    .replace(/<[^>]*>/g, '')
    .replaceAll('&nbsp;', ' ')
    .trim();
}

function getDirectMessageTitle(
  conversation: ChatConversation,
  currentUserEmail?: string | null,
) {
  const participants = conversation.participants ?? [];
  const visibleParticipants = participants.filter(
    (participant) => participant.email !== currentUserEmail,
  );
  const participantsToRender = visibleParticipants.length
    ? visibleParticipants
    : participants;

  if (participantsToRender.length) {
    return participantsToRender
      .map((participant) =>
        (
          participant.display_name ||
          `${participant.first_name ?? ''} ${
            participant.last_name ?? ''
          }`.trim() ||
          participant.email ||
          'Unknown member'
        ).trim(),
      )
      .join(', ');
  }

  return conversation.title;
}

function toActiveChat(
  conversation: ChatConversation,
  currentUserEmail?: string | null,
): ActiveChat {
  return {
    id: conversation.id,
    type: conversation.conversation_type,
    title:
      conversation.conversation_type === 'direct_message'
        ? getDirectMessageTitle(conversation, currentUserEmail)
        : conversation.title,
    description:
      conversation.conversation_type === 'direct_message'
        ? 'Direct message'
        : undefined,
    jobId: conversation.job?.id,
    isArchived: conversation.is_archived,
    isReadOnly: conversation.is_read_only,
  };
}

function mergeOptimisticMessages(
  serverMessages: ChatMessage[],
  optimisticMessages: OptimisticMessage[],
) {
  const serverMessageIds = new Set(serverMessages.map((message) => message.id));
  const roots = serverMessages.map((message) => ({
    ...message,
    replies: [...message.replies].reverse(),
  }));

  if (!optimisticMessages.length) {
    return roots;
  }

  const rootById = new Map(roots.map((message) => [message.id, message]));
  const optimisticRoots: OptimisticMessage[] = [];

  optimisticMessages.forEach((message) => {
    if (serverMessageIds.has(message.id)) {
      return;
    }

    if (message.parent_id) {
      const parentMessage = rootById.get(message.parent_id);

      if (!parentMessage) {
        return;
      }

      if (!parentMessage.replies.some((reply) => reply.id === message.id)) {
        parentMessage.replies = [...parentMessage.replies, message];
      }

      return;
    }

    optimisticRoots.push(message);
  });

  return [...optimisticRoots, ...roots];
}

export function ChatPanel({
  search,
  activeChat,
  filters,
  contextTag,
  onSelectChat,
}: {
  search: string;
  activeChat: ActiveChat;
  filters: ChatFilters;
  contextTag?: ChatContextTagPayload;
  onSelectChat?: (chat: ActiveChat) => void;
}) {
  const activeChatKey = `${activeChat.type}:${activeChat.id}`;
  const [replyState, setReplyState] = useState<ReplyState>({
    chatKey: activeChatKey,
    message: null,
  });
  const [optimisticMessages, setOptimisticMessages] = useState<
    OptimisticMessage[]
  >([]);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const markedReadRef = useRef<string | null>(null);
  const [markConversationRead] = useMarkChatConversationReadMutation();
  const [openDirectMessage] = useOpenDirectMessageMutation();
  useChatRealtimeSubscription(activeChat);
  const defaultParams = useMemo(
    () => ({
      page: 1,
      page_size: 50,
      user_id: filters.userId,
      work_context_type: filters.workContextType,
      work_context_id: filters.workContextId,
    }),
    [filters.userId, filters.workContextId, filters.workContextType],
  );
  const generalMessages = useGetGeneralChatMessagesQuery(
    activeChat.type === 'general_channel' ? defaultParams : skipToken,
  );
  const streamMessages = useGetStreamChatMessagesQuery(
    activeChat.type === 'stream' ? defaultParams : skipToken,
  );
  const unreadMessages = useGetUnreadChatMessagesQuery(
    activeChat.type === 'unread' ? defaultParams : skipToken,
  );
  const mentionMessages = useGetMentionChatMessagesQuery(
    activeChat.type === 'mentions' ? defaultParams : skipToken,
  );
  const jobMessages = useGetJobChatMessagesQuery(
    activeChat.type === 'job_channel' && activeChat.jobId
      ? { jobId: activeChat.jobId, params: defaultParams }
      : skipToken,
  );
  const directMessages = useGetDirectChatMessagesQuery(
    activeChat.type === 'direct_message'
      ? { conversationId: activeChat.id, params: defaultParams }
      : skipToken,
  );
  const activeQuery = {
    general_channel: generalMessages,
    stream: streamMessages,
    unread: unreadMessages,
    mentions: mentionMessages,
    job_channel: jobMessages,
    direct_message: directMessages,
  }[activeChat.type];

  const replyingToMessage =
    replyState.chatKey === activeChatKey ? replyState.message : null;
  const serverMessages = useMemo(
    () => activeQuery.data?.data?.results ?? [],
    [activeQuery.data],
  );
  const activeOptimisticMessages = useMemo(
    () =>
      optimisticMessages.filter((message) => message.chatKey === activeChatKey),
    [activeChatKey, optimisticMessages],
  );
  const messages = useMemo(
    () => mergeOptimisticMessages(serverMessages, activeOptimisticMessages),
    [activeOptimisticMessages, serverMessages],
  );
  const latestMessageId = messages[0]?.id;
  const readConversationId = getConversationReadId(activeChat, messages);

  const createOptimisticMessage = (
    payload: CreateChatMessagePayload,
    parentMessage?: ChatMessage | null,
    displayHtml = payload.body,
  ) => {
    const now = new Date().toISOString();
    const optimisticMessage: OptimisticMessage = {
      id: getTemporaryMessageId(),
      parent_id: parentMessage?.id ?? payload.parent_message_id ?? null,
      author_type: 'user',
      author_user: currentUser
        ? {
            id: 'current-user',
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
            email: currentUser.email,
            display_name:
              `${currentUser.first_name ?? ''} ${
                currentUser.last_name ?? ''
              }`.trim() ||
              currentUser.email ||
              'You',
            avatar: null,
          }
        : null,
      body: payload.body,
      body_format: payload.body_format ?? 'html',
      body_text: getBodyText(displayHtml),
      body_html: displayHtml,
      state: 'active',
      created_at: now,
      updated_at: now,
      source: {
        id: activeChat.id,
        conversation_type: activeChat.type,
        title: activeChat.title,
        is_public: activeChat.type !== 'direct_message',
        is_archived: Boolean(activeChat.isArchived),
        is_read_only: Boolean(activeChat.isReadOnly),
        last_message_at: now,
        job: activeChat.jobId
          ? {
              id: activeChat.jobId,
              name: activeChat.title,
              job_number: 0,
              status: '',
            }
          : null,
      },
      reply_count: 0,
      remaining_reply_count: 0,
      replies: [],
      mentions: {
        users: {},
        work: {},
      },
      isOptimistic: true,
      chatKey: activeChatKey,
    };

    setOptimisticMessages((current) => [optimisticMessage, ...current]);
    return optimisticMessage.id;
  };

  const settleOptimisticMessage = (
    optimisticId: string,
    nextMessage?: ChatMessage | null,
  ) => {
    setOptimisticMessages((current) => {
      if (!nextMessage) {
        return current.filter((message) => message.id !== optimisticId);
      }

      return current.map((message) =>
        message.id === optimisticId
          ? { ...nextMessage, chatKey: message.chatKey }
          : message,
      );
    });
  };

  useEffect(() => {
    if (
      !readConversationId ||
      !latestMessageId ||
      activeQuery.isLoading ||
      activeQuery.isFetching
    ) {
      return;
    }

    const readKey = `${readConversationId}:${latestMessageId}`;
    if (markedReadRef.current === readKey) {
      return;
    }

    markedReadRef.current = readKey;
    void markConversationRead({
      conversationId: readConversationId,
    });
  }, [
    activeQuery.isFetching,
    activeQuery.isLoading,
    latestMessageId,
    markConversationRead,
    readConversationId,
  ]);

  const filteredMessages = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return messages;
    }

    return messages.filter((message) =>
      message.body_text.toLowerCase().includes(query),
    );
  }, [messages, search]);
  const displayMessages = useMemo(
    () => [...filteredMessages].reverse(),
    [filteredMessages],
  );

  const openAuthorDirectMessage = async (userId: string) => {
    if (!onSelectChat) {
      return;
    }

    try {
      const response = await openDirectMessage({
        participant_ids: [userId],
      }).unwrap();

      if (response.data) {
        onSelectChat(toActiveChat(response.data, currentUser?.email));
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <section className={styles.chatPanel}>
      <div className={styles.chatPanel__channelHeader}>
        <div className={styles.chatPanel__headerContent}>
          <div className={styles.chatPanel__titleRow}>
            <span className={styles.chatPanel__eyebrow}>
              {getChatEyebrow(activeChat)}
            </span>
            <span className={styles.chatPanel__titleSeparator}>•</span>
            <Tooltip
              title={activeChat.title}
              wrapperStyle={{
                flex: '1 1 auto',
                minWidth: 0,
                width: 'auto',
              }}
            >
              <h2 className={styles.chatPanel__title}>{activeChat.title}</h2>
            </Tooltip>
          </div>

          {filters.userLabel || filters.workContextLabel ? (
            <div className={styles.chatPanel__activeFilters}>
              {filters.workContextLabel ? (
                <Badge
                  label={`Work: ${filters.workContextLabel}`}
                  tone="default"
                  size="sm"
                />
              ) : null}
              {filters.userLabel ? (
                <Badge
                  label={`Person: ${filters.userLabel}`}
                  tone="default"
                  size="sm"
                />
              ) : null}
            </div>
          ) : null}
        </div>
        <span className={styles.chatPanel__count}>
          {messages.length} {messages.length === 1 ? 'message' : 'messages'}
        </span>
      </div>

      <ChatMessageList
        messages={displayMessages}
        isLoading={activeQuery.isLoading}
        onReply={(message) =>
          setReplyState({ chatKey: activeChatKey, message })
        }
        showSources={activeChat.type === 'stream'}
        onSelectSource={onSelectChat}
        currentUserEmail={currentUser?.email}
        onOpenDirectMessage={onSelectChat ? openAuthorDirectMessage : undefined}
        onOpenUserProfile={setProfileUserId}
      />
      <UserProfileDrawer
        open={Boolean(profileUserId)}
        userId={profileUserId}
        currentUserEmail={currentUser?.email}
        onClose={() => setProfileUserId(null)}
        onExited={() => setProfileUserId(null)}
        onMessage={onSelectChat ? openAuthorDirectMessage : undefined}
      />
      <ChatComposer
        activeChat={activeChat}
        replyingToMessage={replyingToMessage}
        contextTag={contextTag}
        onCancelReply={() =>
          setReplyState({ chatKey: activeChatKey, message: null })
        }
        onCreateOptimisticMessage={createOptimisticMessage}
        onSettleOptimisticMessage={settleOptimisticMessage}
      />
    </section>
  );
}
